import type {
  MCPServerFormValues,
  ToolSettings,
} from "@app/components/actions/mcp/forms/mcpServerFormSchema";
import {
  canToolUseMediumStakeLevel,
  encodeMCPToolNameForForm,
  getEffectiveToolSettings,
} from "@app/components/actions/mcp/forms/mcpServerFormSchema";
import { BulkSelectionBar } from "@app/components/shared/BulkSelectionBar";
import type { MCPToolStakeLevelType } from "@app/lib/actions/constants";
import { MCP_TOOL_STAKE_LEVELS } from "@app/lib/actions/constants";
import type { MCPServerViewType } from "@app/lib/api/mcp";
import {
  MCP_TOOL_STAKE_DESCRIPTIONS,
  MCP_TOOL_STAKE_LABELS,
  MCP_TOOL_STAKE_SHORT_LABELS,
} from "@app/lib/client/tool_stakes_descriptions";
import { asDisplayName } from "@app/types/shared/utils/string_utils";
import {
  Button,
  Check,
  Checkbox,
  ContentMessage,
  cn,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
  InfoCircle,
  Label,
  ListGroup,
  ListItem,
  SearchInput,
  SliderToggle,
  XClose,
} from "@dust-tt/sparkle";
import { plural } from "@lingui/core/macro";
import { Trans, useLingui } from "@lingui/react/macro";
import { useLayoutEffect, useMemo, useRef, useState } from "react";
import { useFormContext } from "react-hook-form";

type ToolDefinition = MCPServerViewType["server"]["tools"][number];

export interface ToolsAndStakesController {
  search: string;
  setSearch: (search: string) => void;
  tools: ToolDefinition[];
  visibleTools: ToolDefinition[];
  selectedToolNames: string[];
  isSelected: (toolName: string) => boolean;
  toggleSelected: (toolName: string) => void;
  areAllVisibleSelected: boolean;
  toggleSelectAllVisible: () => void;
  clearSelection: () => void;
  getSettings: (toolName: string) => ToolSettings;
  updateTool: (toolName: string, patch: Partial<ToolSettings>) => void;
  selectionStakeLevels: MCPToolStakeLevelType[];
  applyToSelection: (patch: Partial<ToolSettings>) => void;
}

/**
 * @cc [owner:id13,label:product] tool-selection-persistence
 * Tool selection MUST survive search changes, including clearing search, and batch stake or state updates.
 */
export function useToolsAndStakesController(
  mcpServerView: MCPServerViewType | null
): ToolsAndStakesController {
  const form = useFormContext<MCPServerFormValues>();
  const [search, setSearch] = useState("");
  const [selectedToolNames, setSelectedToolNames] = useState<string[]>([]);
  const serverViewId = mcpServerView?.sId ?? null;
  const [previousServerViewId, setPreviousServerViewId] =
    useState(serverViewId);

  if (serverViewId !== previousServerViewId) {
    setPreviousServerViewId(serverViewId);
    setSearch("");
    setSelectedToolNames([]);
  }

  const tools = useMemo(
    () => mcpServerView?.server.tools ?? [],
    [mcpServerView]
  );

  const visibleTools = useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (!needle) {
      return tools;
    }
    return tools.filter(
      (tool) =>
        tool.name.toLowerCase().includes(needle) ||
        asDisplayName(tool.name).toLowerCase().includes(needle) ||
        (tool.description ?? "").toLowerCase().includes(needle)
    );
  }, [tools, search]);

  const watchedToolSettings = form.watch("toolSettings");

  const getSettings = (toolName: string): ToolSettings => {
    const fromForm = watchedToolSettings?.[encodeMCPToolNameForForm(toolName)];
    if (fromForm) {
      return fromForm;
    }
    return mcpServerView
      ? getEffectiveToolSettings(mcpServerView, toolName)
      : { enabled: true, permission: "low" };
  };

  const setSettings = (toolName: string, next: ToolSettings) => {
    form.setValue(`toolSettings.${encodeMCPToolNameForForm(toolName)}`, next, {
      shouldDirty: true,
      shouldTouch: true,
    });
  };

  const updateTool = (toolName: string, patch: Partial<ToolSettings>) => {
    setSettings(toolName, { ...getSettings(toolName), ...patch });
  };

  const selectedSet = new Set(selectedToolNames);

  const areAllVisibleSelected =
    visibleTools.length > 0 &&
    visibleTools.every((tool) => selectedSet.has(tool.name));

  const selectionStakeLevels = MCP_TOOL_STAKE_LEVELS.filter(
    (stakeLevel) =>
      stakeLevel !== "medium" ||
      selectedToolNames.some((toolName) =>
        mcpServerView
          ? canToolUseMediumStakeLevel(mcpServerView.server, toolName)
          : false
      )
  );

  const applyToSelection = (patch: Partial<ToolSettings>) => {
    for (const toolName of selectedToolNames) {
      // Medium is only meaningful for tools declaring arguments that require
      // approval; applying it to the rest would persist a stake they cannot run at.
      if (
        patch.permission === "medium" &&
        mcpServerView &&
        !canToolUseMediumStakeLevel(mcpServerView.server, toolName)
      ) {
        continue;
      }
      updateTool(toolName, patch);
    }
  };

  return {
    search,
    setSearch,
    tools,
    visibleTools,
    selectedToolNames,
    isSelected: (toolName: string) => selectedSet.has(toolName),
    toggleSelected: (toolName: string) =>
      setSelectedToolNames((previous) =>
        previous.includes(toolName)
          ? previous.filter((name) => name !== toolName)
          : [...previous, toolName]
      ),
    areAllVisibleSelected,
    toggleSelectAllVisible: () => {
      const visibleToolNames = new Set(visibleTools.map((tool) => tool.name));
      setSelectedToolNames((previous) => {
        if (areAllVisibleSelected) {
          return previous.filter((name) => !visibleToolNames.has(name));
        }
        const previousNames = new Set(previous);
        return [
          ...previous,
          ...visibleTools
            .map((tool) => tool.name)
            .filter((name) => !previousNames.has(name)),
        ];
      });
    },
    clearSelection: () => setSelectedToolNames([]),
    getSettings,
    updateTool,
    selectionStakeLevels,
    applyToSelection,
  };
}

interface MCPServerDetailsToolsProps {
  mcpServerView: MCPServerViewType;
  controller: ToolsAndStakesController;
}

/**
 * Every operation the server exposes, each one switchable and carrying the stake
 * level that decides whether Dust asks before running it. A real MCP server
 * declares dozens, hence the search and the per-row selection: batch actions
 * apply to the selection even when the search hides some selected tools.
 */
export function MCPServerDetailsTools({
  mcpServerView,
  controller,
}: MCPServerDetailsToolsProps) {
  const { t } = useLingui();
  const {
    search,
    setSearch,
    tools,
    visibleTools,
    isSelected,
    toggleSelected,
    areAllVisibleSelected,
    toggleSelectAllVisible,
    getSettings,
    updateTool,
  } = controller;

  if (tools.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">
        <Trans>This server exposes no tools.</Trans>
      </p>
    );
  }

  const toolCount = tools.length;
  const visibleToolCount = visibleTools.length;

  return (
    <div className="flex flex-col gap-4">
      <div className="heading-base text-foreground">
        {visibleToolCount === toolCount
          ? t`${plural(toolCount, { one: "# tool available", other: "# tools available" })}`
          : t`${visibleToolCount} of ${plural(toolCount, { one: "# tool available", other: "# tools available" })}`}
      </div>

      <ContentMessage
        className="w-full"
        variant="blue"
        size="lg"
        icon={InfoCircle}
        title={t`User approval settings`}
      >
        <ul>
          {MCP_TOOL_STAKE_LEVELS.map((stakeLevel) => {
            const stakeLabel = t(MCP_TOOL_STAKE_SHORT_LABELS[stakeLevel]);
            const stakeDescription = t(MCP_TOOL_STAKE_DESCRIPTIONS[stakeLevel]);
            return (
              <li key={stakeLevel}>
                <Trans>
                  <b>{stakeLabel}</b>: {stakeDescription}
                </Trans>
              </li>
            );
          })}
        </ul>
      </ContentMessage>

      <div className="flex items-center gap-2">
        <SearchInput
          name="tool-filter"
          placeholder={t`Search tools`}
          className="grow"
          value={search}
          onChange={setSearch}
        />
        <Button
          size="sm"
          variant="outline"
          label={areAllVisibleSelected ? t`Deselect all` : t`Select all`}
          disabled={visibleTools.length === 0}
          onClick={toggleSelectAllVisible}
        />
      </div>

      {visibleTools.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          <Trans>No tool matches that search.</Trans>
        </p>
      ) : (
        <ListGroup>
          {visibleTools.map((tool) => (
            <ToolRow
              key={tool.name}
              tool={tool}
              settings={getSettings(tool.name)}
              stakeLevels={MCP_TOOL_STAKE_LEVELS.filter(
                (stakeLevel) =>
                  stakeLevel !== "medium" ||
                  canToolUseMediumStakeLevel(mcpServerView.server, tool.name)
              )}
              isSelected={isSelected(tool.name)}
              onSelectedChange={() => toggleSelected(tool.name)}
              onPatch={(patch) => updateTool(tool.name, patch)}
            />
          ))}
        </ListGroup>
      )}
    </div>
  );
}

interface ToolRowProps {
  tool: ToolDefinition;
  settings: ToolSettings;
  stakeLevels: ReadonlyArray<MCPToolStakeLevelType>;
  isSelected: boolean;
  onSelectedChange: () => void;
  onPatch: (patch: Partial<ToolSettings>) => void;
}

/**
 * One operation: ticked to put it in the batch, switched off to take it away
 * from every agent. Switching it off recedes the row and drops the stake line,
 * since a disabled operation has nothing to ask about.
 */
function ToolRow({
  tool,
  settings,
  stakeLevels,
  isSelected,
  onSelectedChange,
  onPatch,
}: ToolRowProps) {
  const { t } = useLingui();
  const toolDisplayName = asDisplayName(tool.name);
  const checkboxId = `select-tool-${encodeMCPToolNameForForm(tool.name)}`;

  return (
    <ListItem
      className={cn("flex-col gap-2", !settings.enabled && "bg-app-background")}
    >
      <div className="flex w-full items-center gap-2">
        {/* The label makes the name a hit target for the checkbox too. */}
        <Label
          htmlFor={checkboxId}
          className="flex grow cursor-pointer items-center gap-2"
        >
          <Checkbox
            id={checkboxId}
            checked={isSelected}
            aria-label={
              isSelected
                ? t`Deselect ${toolDisplayName}`
                : t`Select ${toolDisplayName}`
            }
            onCheckedChange={onSelectedChange}
          />
          <span
            className={cn(
              "heading-base",
              settings.enabled ? "text-foreground" : "text-muted-foreground"
            )}
          >
            {toolDisplayName}
          </span>
        </Label>
        <SliderToggle
          selected={settings.enabled}
          onClick={() => onPatch({ enabled: !settings.enabled })}
        />
      </div>

      {/* pl-6 clears the checkbox and its gap, so everything below the name
          lines up with the name rather than with the tick. */}
      {/* Keyed on the text so a re-synced description is measured afresh
          rather than reusing the previous one's overflow verdict. */}
      {tool.description && (
        <ClampedDescription
          key={tool.description}
          className="pl-6"
          description={tool.description}
        />
      )}

      {settings.enabled && (
        <div className="flex w-full items-center justify-between gap-2 pl-6">
          <Label isMuted>
            <Trans>Stake</Trans>
          </Label>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                size="sm"
                variant="outline"
                label={t(MCP_TOOL_STAKE_LABELS[settings.permission])}
                isSelect
              />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {stakeLevels.map((stakeLevel) => (
                <DropdownMenuItem
                  key={stakeLevel}
                  label={t(MCP_TOOL_STAKE_LABELS[stakeLevel])}
                  onClick={() => onPatch({ permission: stakeLevel })}
                />
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      )}
    </ListItem>
  );
}

/**
 * A description a server declares, for itself or a tool, kept to two lines.
 * These run to a paragraph in the wild, and thirty paragraphs would bury the
 * list they are meant to explain.
 */
interface ClampedDescriptionProps {
  description: string;
  className?: string;
}

export function ClampedDescription({
  description,
  className,
}: ClampedDescriptionProps) {
  const { t } = useLingui();
  const textRef = useRef<HTMLParagraphElement>(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const [overflows, setOverflows] = useState(false);

  // Only measured while clamped: expanding lifts the clamp, so the same
  // measurement would come back equal and retract the "Show less" button.
  useLayoutEffect(() => {
    const element = textRef.current;
    if (element && !isExpanded) {
      setOverflows(element.scrollHeight > element.clientHeight + 1);
    }
  }, [isExpanded]);

  return (
    <div className={cn("flex flex-col items-start gap-1", className)}>
      <p
        ref={textRef}
        className={cn(
          "whitespace-pre-wrap break-words text-sm italic text-muted-foreground",
          !isExpanded && "line-clamp-2"
        )}
      >
        {description}
      </p>
      {overflows && (
        <Button
          size="xs"
          variant="ghost-secondary"
          label={isExpanded ? t`Show less` : t`Show more`}
          onClick={() => setIsExpanded(!isExpanded)}
        />
      )}
    </div>
  );
}

interface MCPServerDetailsToolsBulkBarProps {
  controller: ToolsAndStakesController;
}

export function MCPServerDetailsToolsBulkBar({
  controller,
}: MCPServerDetailsToolsBulkBarProps) {
  const { t } = useLingui();
  const {
    selectedToolNames,
    visibleTools,
    clearSelection,
    selectionStakeLevels,
    applyToSelection,
  } = controller;
  const toolCount = visibleTools.length;

  return (
    <BulkSelectionBar
      selectedCount={selectedToolNames.length}
      selectAllLabel={t`Select all ${plural(toolCount, { one: "# tool", other: "# tools" })}`}
      canSelectAll={false}
      onSelectAll={() => {}}
      onClear={clearSelection}
    >
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button size="sm" variant="primary" isSelect label={t`Set stake`} />
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" side="top">
          <DropdownMenuLabel label={t`Set stake`} />
          {selectionStakeLevels.map((stakeLevel) => (
            <DropdownMenuItem
              key={stakeLevel}
              label={t(MCP_TOOL_STAKE_LABELS[stakeLevel])}
              onClick={() => applyToSelection({ permission: stakeLevel })}
            />
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button size="sm" variant="primary" isSelect label={t`State`} />
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" side="top">
          <DropdownMenuLabel label={t`State`} />
          <DropdownMenuItem
            icon={Check}
            label={t`Enable`}
            onClick={() => applyToSelection({ enabled: true })}
          />
          <DropdownMenuItem
            icon={XClose}
            label={t`Disable`}
            onClick={() => applyToSelection({ enabled: false })}
          />
        </DropdownMenuContent>
      </DropdownMenu>
    </BulkSelectionBar>
  );
}
